/**
 * The player shell (R7): boot the shipped pack, then hand the session to the
 * live view.
 *
 * @remarks
 * The Phase 0 static boot transcript is retired — this component now owns
 * exactly the three boot phases (loading / ready / failed) and nothing else;
 * the playing is `SessionViewComponent`'s, the impure edges are
 * `boot-session.ts`'s. A failed boot renders the failure text plainly: the
 * shipped pack failing to load is a build defect, and hiding it behind
 * fiction would only slow the fix.
 *
 * @packageDocumentation
 */

import type { Surface } from '@orpheus/content-schema';

import { selectAmbientAudioVolume, selectIsAmbientAudioEnabled } from '@orpheus/game-store';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AccountControl } from './account-control.js';
import {
	type AmbiencePlayer,
	ambientGainFromSetting,
	browserAmbienceDependencies,
	createAmbiencePlayer,
} from './ambience-player.js';
import { type BootedSession, bootSession } from './boot-session.js';
import { CreatorOnboarding } from './creator-onboarding.js';
import { HandoffScreen } from './handoff-screen.js';
import { EngineMastheadComponent, GameMastheadComponent } from './masthead-component.js';
import { SessionViewComponent } from './session-view.js';
import { type CreatorArrival, type PurchaseArrival, readCreatorArrival, readPurchaseArrival } from './store-client.js';
import { TitleScreenSection } from './title-screen-section.js';

/**
 * D170: whether a hostname has a host record — asked of the play Worker's
 * public door (D162), which is where the handoff screen learns that a
 * return address is a page we serve. Without a play Worker nothing is
 * served, and no token leaves.
 */
const isServedHostname = async (hostname: string): Promise<boolean> => {
	const playUrl: unknown = import.meta.env['VITE_PLAY_URL'];

	if (typeof playUrl !== 'string' || playUrl.length === 0) {
		return false;
	}

	const answer = await fetch(`${playUrl.replace(/\/$/u, '')}/hosts/${encodeURIComponent(hostname)}`);

	return answer.ok;
};

/** The shell's boot phases. */
type BootPhase =
	| { readonly message: string; readonly phase: 'failed' }
	| { readonly phase: 'loading' }
	| { readonly phase: 'ready'; readonly session: BootedSession };

/** What the page was arrived at with (D165, D165b), read once. */
interface Arrivals {
	readonly creatorArrival: CreatorArrival | undefined;
	readonly purchaseArrival: PurchaseArrival | undefined;
}

/** Read the store's arrivals off the query and drop them from the address bar. */
const readArrivals = (): Arrivals => {
	const arrivals: Arrivals = { creatorArrival: readCreatorArrival(window.location.search), purchaseArrival: readPurchaseArrival(window.location.search) };

	if (arrivals.creatorArrival !== undefined || arrivals.purchaseArrival !== undefined) {
		const url = new URL(window.location.href);

		url.searchParams.delete('purchase');
		url.searchParams.delete('creator');
		window.history.replaceState(window.history.state, '', url);
	}

	return arrivals;
};

/** The page's own chrome colors, one per surface: what a phone paints around the page (`theme-color`), matching `--color-background`. */
const THEME_COLOR_BY_SURFACE: Readonly<Record<Surface, string>> = { chart: '#2d3436', terminal: '#0b0e11' };

/**
 * D203/D204: dress the DOCUMENT in the game's surface — the attribute the
 * stylesheet reads sits on the root, so the ground under the shell, the fixed
 * title screen and every dialog change with it, not only the column; and the
 * browser's own chrome follows through `theme-color`. `terminal` for a game
 * that says nothing, exactly as the loader defaults it.
 *
 * @param surface - The surface the description names
 */
const dressDocument = (surface: Surface): void => {
	document.documentElement.dataset['surface'] = surface;
	document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR_BY_SURFACE[surface]);
};

/**
 * Render the player shell.
 *
 * @returns The application root
 */
export const AppComponent = () => {
	const [bootPhase, setBootPhase] = useState<BootPhase>({ phase: 'loading' });
	// R35: the title gate. It exists for the fiction (a cold terminal you wake)
	// and for the browser (audio needs a user gesture; the wake IS one). D116
	// made the wake its own STEP: BEGIN only appears once the terminal is
	// awake, so the theme is heard on the landing page rather than skippable
	// by a fast click (Mike's verification finding). The player object is
	// created once and never touches AudioContext until a gesture calls
	// `ensureStarted`.
	const [hasWokenTerminal, setHasWokenTerminal] = useState(false);
	const [hasEnteredSession, setHasEnteredSession] = useState(false);
	const ambiencePlayer: AmbiencePlayer = useMemo(() => createAmbiencePlayer(browserAmbienceDependencies()), []);
	// D165: back from a checkout, or (D165b) an ask to onboard as a creator /
	// back from Stripe's onboarding — read once, and dropped from the address
	// bar so a reload does not act twice. `?seed=` and the rest stay.
	const arrivalsRef = useRef<Arrivals | null>(null);

	arrivalsRef.current ??= readArrivals();

	const { creatorArrival, purchaseArrival } = arrivalsRef.current;

	useEffect(() => {
		let isDisposed = false;

		bootSession(window.location.search, { hearTurn: ambiencePlayer.hearTurn })
			.then((session) => {
				if (!isDisposed) {
					// D132: the tab is named after the GAME, from its manifest — the
					// static document title is the engine's, and only until now.
					document.title = session.game.title;
					dressDocument(session.game.surface ?? 'terminal');
					setBootPhase({ phase: 'ready', session });
				}
			})
			.catch((bootError: unknown) => {
				if (!isDisposed) {
					setBootPhase({
						message: bootError instanceof Error ? bootError.message : String(bootError),
						phase: 'failed',
					});
				}
			});

		return () => {
			isDisposed = true;
		};
	}, [ambiencePlayer]);

	/*
	 * The masthead is authored once here and PLACED by whoever is rendering.
	 * Before a session exists it sits at the top of the shell, because there is
	 * nothing else to look at. Once the session is live it is handed to the
	 * session view, which puts it inside the scrollback — so it scrolls away
	 * with the opening beats and never comes back, giving its vertical space to
	 * the plate and the prose. You need a game's title once.
	 *
	 * D132: WHOSE masthead depends on whether there is a game yet. Booting and
	 * failing are the engine speaking about itself; a loaded bundle is the game
	 * speaking, in its own mark or its own name.
	 */
	const masthead =
		bootPhase.phase === 'ready' ? (
			<GameMastheadComponent game={bootPhase.session.game} />
		) : (
			<EngineMastheadComponent />
		);

	return (
		<main className="player-shell">
			{bootPhase.phase === 'ready' ? undefined : masthead}
			{bootPhase.phase === 'loading' ? <p className="player-shell__status">ESTABLISHING LINK…</p> : undefined}
			{bootPhase.phase === 'failed' ? (
				<p className="player-shell__status player-shell__status--failed" role="alert">
					BOOT FAILED: {bootPhase.message}
				</p>
			) : undefined}
			{/* D165b: creator onboarding, on this app until the studio exists — never a button, only an arrival. */}
			{bootPhase.phase === 'ready' && creatorArrival !== undefined && bootPhase.session.storefront !== undefined ? (
				<CreatorOnboarding
					arrival={creatorArrival}
					navigate={(url) => {
						window.location.assign(url);
					}}
					returnUrl={window.location.href}
					storefront={bootPhase.session.storefront}
				/>
			) : undefined}
			{/* D170: asked by a creator's domain to sign a player in — this page is the handoff, not the game. */}
			{bootPhase.phase === 'ready' && bootPhase.session.handoff.returnUrl !== undefined && bootPhase.session.identity !== undefined ? (
				<HandoffScreen
					identity={bootPhase.session.identity}
					isServedHostname={isServedHostname}
					onIdentityChanged={() => {
						window.location.reload();
					}}
					returnUrl={bootPhase.session.handoff.returnUrl}
				/>
			) : undefined}
			{bootPhase.phase === 'ready' && bootPhase.session.handoff.returnUrl === undefined && !hasEnteredSession ? (
				<TitleScreenSection
					ambiencePlayer={ambiencePlayer}
					hasWokenTerminal={hasWokenTerminal}
					masthead={masthead}
					onBegin={() => {
						setHasEnteredSession(true);
					}}
					onWake={() => {
						ambiencePlayer.ensureStarted();
						// D118: the wake re-asserts the STORE's truth before the theme
						// breathes in — a reload that persisted a different volume, or
						// a mute, must land on the title exactly as it was left, not
						// at the player's built-in defaults.
						const preferences = bootPhase.session.store.getState();

						ambiencePlayer.setVolume(ambientGainFromSetting(selectAmbientAudioVolume(preferences)));
						ambiencePlayer.setEnabled(selectIsAmbientAudioEnabled(preferences));
						// The theme is the game's, already a target (D156): silence is a
						// TARGET (§6.2), so a quiet title still has the player aimed somewhere.
						ambiencePlayer.setTarget(bootPhase.session.game.titleScreen.theme);
						setHasWokenTerminal(true);
					}}
					game={bootPhase.session.game}
					store={bootPhase.session.store}
				/>
			) : undefined}
			{bootPhase.phase === 'ready' && bootPhase.session.handoff.returnUrl === undefined && hasEnteredSession ? (
				<SessionViewComponent
					// D157: the account control exists only when the build has an
					// identity Worker. A sign-in or sign-out changes who the play
					// cookie means, and the open session was the previous owner's —
					// so the shell opens again from the top, as the new one.
					account={
						bootPhase.session.identity === undefined ? undefined : (
							<AccountControl
								arrivalNotice={bootPhase.session.handoff.refusal}
								arrivalSearch={window.location.search}
								handoffDepartureUrl={bootPhase.session.handoff.departureUrl}
								identity={bootPhase.session.identity}
								storefront={bootPhase.session.storefront}
								onArrivalRead={() => {
									// The link's `?error=` has been read; `?seed=` and the rest stay.
									const url = new URL(window.location.href);

									url.searchParams.delete('error');
									url.searchParams.delete('error_description');
									window.history.replaceState(window.history.state, '', url);
								}}
								onIdentityChanged={() => {
									window.location.reload();
								}}
							/>
						)
					}
					ambiencePlayer={ambiencePlayer}
					game={bootPhase.session.game}
					host={bootPhase.session.host}
					masthead={masthead}
					purchaseArrival={purchaseArrival}
					store={bootPhase.session.store}
					storefront={bootPhase.session.storefront}
				/>
			) : undefined}
		</main>
	);
};
